import React, { useState } from "react";

import type { variant } from "@/src/components/custom/custom-dataset-select";

import { iconOptions } from "@pages/moments/icon-options";

import {
  Box,
  Group,
  Text,
  Title,
  TextInput,
  NumberInput,
  Textarea,
  TagsInput,
  Grid,
} from "@mantine/core";

import { Dropzone } from "@mantine/dropzone";

import {
  CustomSelect,
  CustomMultiSelect,
  CustomAutocomplete,
  CustomColorInput,
  CustomDatePickerInput,
  CustomDateTimePickerInput,
  CustomCounterMultiSelect,
  CustomDatasetSelect,
  CustomIconSelect,
  CustomOutput,
  CustomTimeInput,
} from "@components/custom";

type EventOption = {
  id: string;
  name: string;
  status: variant;
  category?: string;
};

const createDataset = (
  id: string,
  name: string,
  status: variant,
  category?: string,
): EventOption => ({ id, name, status, category });

const mockDatasets = [
  createDataset("100", "PTO T100 Singapore 2025", "live", "Men"),
  createDataset("101", "PTO T100 Miami 2024", "upcoming"),
  createDataset("102", "PTO T100 Ibiza 2024", "completed", "Women"),
  createDataset("103", "PTO T100 French Riviera 2025", "completed", "Men"),
  createDataset("104", "PTO T100 - San Francisco 2025", "replaying", "Men"),
];

const iconOptionsForPicker = iconOptions
  .filter((o) => o?.value && o?.label)
  .map((o) => ({
    value: o.value,
    label: o.label,
    icon: <></>,
  }));

export const FormFieldsStylesheet: React.FC = () => {
  const [selectedIcons, setSelectedIcons] = useState<
    Record<number, string | null>
  >({});

  return (
    <>
      <Title order={2} mb="md">
        Form Controls
      </Title>

      <Grid mb="xl" align="stretch">
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <CustomOutput
            placeholderText='Please enter your output. Type ":" to insert a variable'
            labelText="Custom Output"
            required
            mentionItems={{
              ":": [
                "Participant",
                "Dataset",
                "Heart Rate",
                "Power",
                "Speed",
                "Latitude",
                "Longitude",
                "Split",
                "Lap",
              ],
            }}
          />
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 6 }}>
          <CustomOutput
            placeholderText='Please enter your output. Type ":" to insert a variable'
            labelText="Custom Output"
            required
            error="Error message"
            mentionItems={{
              ":": [
                "Participant",
                "Dataset",
                "Heart Rate",
                "Power",
                "Speed",
                "Latitude",
                "Longitude",
                "Split",
                "Lap",
              ],
            }}
          />
        </Grid.Col>
      </Grid>

      {/* Text Input */}
      <Grid mb="xl" align="stretch">
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TextInput label="Input" placeholder="Placeholder" required />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TextInput label="Input" placeholder="Disabled" disabled />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TextInput
            label="Input"
            placeholder="Placeholder"
            error="This field is required"
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TextInput
            variant="dark"
            label="Input Dark"
            placeholder="Placeholder"
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TextInput
            variant="darker"
            label="Input Darker"
            placeholder="Placeholder"
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TextInput
            variant="sky"
            label="Input Sky"
            placeholder="Placeholder"
          />
        </Grid.Col>
      </Grid>

      {/* Select */}
      <Grid mb="xl" align="stretch">
        {["", "Disabled", "Error", "Dark", "Darker", "Sky"].map(
          (variant, i) => (
            <Grid.Col key={i} span={{ base: 12, sm: 6 }}>
              <CustomSelect
                required
                label={`Select ${variant}`}
                placeholder="Placeholder"
                data={["Option 1", "Option 2", "Option 3", "Option 4"]}
                disabled={variant === "Disabled"}
                error={
                  variant === "Error" ? "This field is required" : undefined
                }
                variant={
                  variant === "Dark"
                    ? "dark"
                    : variant === "Darker"
                      ? "darker"
                      : variant === "Sky"
                        ? "sky"
                        : "default"
                }
              />
            </Grid.Col>
          ),
        )}
      </Grid>

      {/* MultiSelect */}
      <Grid mb="xl" align="stretch">
        {["", "Disabled", "Error", "Dark", "Darker", "Sky"].map(
          (variant, i) => (
            <Grid.Col key={i} span={{ base: 12, sm: 6 }}>
              <CustomMultiSelect
                required
                label={`MultiSelect ${variant}`}
                placeholder="Placeholder"
                data={["Option 1", "Option 2", "Option 3", "Option 4"]}
                disabled={variant === "Disabled"}
                error={
                  variant === "Error" ? "This field is required" : undefined
                }
                variant={
                  variant === "Dark"
                    ? "dark"
                    : variant === "Darker"
                      ? "darker"
                      : variant === "Sky"
                        ? "sky"
                        : "default"
                }
              />
            </Grid.Col>
          ),
        )}
      </Grid>
      {/* Counter Multiselect */}
      <Grid mb="xl" align="stretch">
        {["", "Disabled", "Error", "Dark", "Darker", "Sky"].map(
          (variant, i) => (
            <Grid.Col key={i} span={{ base: 12, sm: 6 }}>
              <CustomCounterMultiSelect
                required
                label={`Counter MultiSelect ${variant}`}
                placeholder="Placeholder"
                data={["Option 1", "Option 2", "Option 3", "Option 4"]}
                disabled={variant === "Disabled"}
                error={
                  variant === "Error" ? "This field is required" : undefined
                }
                variant={
                  variant === "Dark"
                    ? "dark"
                    : variant === "Darker"
                      ? "darker"
                      : variant === "Sky"
                        ? "sky"
                        : "default"
                }
              />
            </Grid.Col>
          ),
        )}
      </Grid>

      {/* Number Input */}
      <Grid mb="xl" align="stretch">
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <NumberInput label="Number Input" defaultValue={1} required />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <NumberInput label="Number Input" defaultValue={1} disabled />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <NumberInput
            label="Number Input"
            defaultValue={1}
            error="Error message"
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <NumberInput
            variant="dark"
            label="Number Input Dark"
            defaultValue={1}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <NumberInput
            variant="darker"
            label="Number Input Darker"
            defaultValue={1}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <NumberInput
            variant="sky"
            label="Number Input Sky"
            defaultValue={1}
          />
        </Grid.Col>
      </Grid>

      {/* Autocomplete */}
      <Grid mb="xl" align="stretch">
        {["", "Disabled", "Error", "Dark", "Darker", "Sky"].map(
          (variant, i) => (
            <Grid.Col key={i} span={{ base: 12, sm: 6 }}>
              <CustomAutocomplete
                required
                label={`Autocomplete ${variant}`}
                placeholder="Placeholder"
                data={["Option 1", "Option 2", "Option 3", "Option 4"]}
                disabled={variant === "Disabled"}
                error={variant === "Error" ? "Error message" : undefined}
                variant={
                  variant === "Dark"
                    ? "dark"
                    : variant === "Darker"
                      ? "darker"
                      : variant === "Sky"
                        ? "sky"
                        : "default"
                }
              />
            </Grid.Col>
          ),
        )}
      </Grid>

      {/* Color Input */}
      <Grid mb="xl" align="stretch">
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <CustomColorInput
            required
            label="Color Input"
            placeholder="Placeholder"
            format="hexa"
            swatchesPerRow={10}
            swatches={[
              "#01eca4",
              "#dce503",
              "#fd9e00",
              "#fa6724",
              "#fa25bf",
              "#946be0",
              "#647cff",
              "#766d7e",
              "#24063e",
            ]}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <CustomColorInput
            label="Color Input"
            placeholder="Disabled"
            format="hexa"
            disabled
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <CustomColorInput
            label="Color Input"
            placeholder="Placeholder"
            format="hexa"
            error="Error message"
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <CustomColorInput
            label="Color Input Dark"
            placeholder="Placeholder"
            format="hexa"
            variant="dark"
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <CustomColorInput
            label="Color Input Darker"
            placeholder="Placeholder"
            format="hexa"
            variant="darker"
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <CustomColorInput
            label="Color Input Sky"
            placeholder="Placeholder"
            format="hexa"
            variant="sky"
          />
        </Grid.Col>
      </Grid>

      {/* Date Picker */}
      <Grid mb="xl" align="stretch">
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <CustomDatePickerInput
            label="Date Picker"
            placeholder="Select date"
            defaultValue={new Date()}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <CustomDatePickerInput
            label="Date Picker"
            placeholder="Select date"
            defaultValue={new Date()}
            disabled
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <CustomDatePickerInput
            label="Date Picker"
            placeholder="Select date"
            defaultValue={new Date()}
            error="Error message"
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <CustomDatePickerInput
            label="Date Picker Dark"
            placeholder="Select date"
            defaultValue={new Date()}
            variant="dark"
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <CustomDatePickerInput
            label="Date Picker Darker"
            placeholder="Select date"
            defaultValue={new Date()}
            variant="darker"
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <CustomDatePickerInput
            label="Date Picker Sky"
            placeholder="Select date"
            defaultValue={new Date()}
            variant="sky"
          />
        </Grid.Col>
      </Grid>

      {/* Date Time Picker */}
      <Grid mb="xl" align="stretch">
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <CustomDateTimePickerInput
            label="Date Time Picker"
            defaultValue={new Date()}
            placeholder="Select a date and time"
            required
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <CustomDateTimePickerInput
            label="Date Time Picker"
            defaultValue={new Date()}
            placeholder="Select a date and time"
            required
            disabled
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <CustomDateTimePickerInput
            label="Date Time Picker"
            defaultValue={new Date()}
            placeholder="Select a date and time"
            required
            error="Error message"
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <CustomDateTimePickerInput
            label="Date Time Picker Dark"
            defaultValue={new Date()}
            placeholder="Select a date and time"
            required
            variant="dark"
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <CustomDateTimePickerInput
            label="Date Time Picker Darker"
            defaultValue={new Date()}
            placeholder="Select a date and time"
            required
            variant="darker"
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <CustomDateTimePickerInput
            label="Date Time Picker Sky"
            defaultValue={new Date()}
            placeholder="Select a date and time"
            required
            variant="sky"
          />
        </Grid.Col>
      </Grid>

      {/* Time Input */}
      <Grid mb="xl" align="stretch">
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <CustomTimeInput label="Time input" required maxH={2} maxMin={30} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <CustomTimeInput label="Time input" required disabled />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <CustomTimeInput label="Time input" required error="Error message" />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <CustomTimeInput label="Time input Dark" required variant="dark" />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <CustomTimeInput
            label="Time input Darker"
            required
            variant="darker"
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <CustomTimeInput label="Time input Sky" required variant="sky" />
        </Grid.Col>
      </Grid>

      {/* Textarea */}
      <Grid mb="xl" align="stretch">
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Textarea required label="Textarea" placeholder="Textarea" autosize />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Textarea label="Textarea" placeholder="Textarea" autosize disabled />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Textarea
            label="Textarea"
            placeholder="Textarea"
            autosize
            error="Error message"
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Textarea
            label="Textarea Dark"
            placeholder="Textarea"
            autosize
            variant="dark"
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Textarea
            label="Textarea Darker"
            placeholder="Textarea"
            autosize
            variant="darker"
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Textarea
            label="Textarea Sky"
            placeholder="Textarea"
            autosize
            variant="sky"
          />
        </Grid.Col>
      </Grid>
      {/* Tags Input */}
      <Grid mb="xl" align="stretch">
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TagsInput required label="Tags Input" placeholder="Add tags" />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TagsInput
            required
            label="Tags Input"
            placeholder="Add tags"
            disabled
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TagsInput
            required
            label="Tags Input"
            placeholder="Add tags"
            error="Error message"
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TagsInput
            required
            variant="dark"
            label="Tags Input Dark"
            placeholder="Add tags"
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TagsInput
            required
            variant="darker"
            label="Tags Input Darker"
            placeholder="Add tags"
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <TagsInput
            required
            variant="sky"
            label="Tags Input Sky"
            placeholder="Add tags"
          />
        </Grid.Col>
      </Grid>

      {/* Icon Select */}
      <Grid mb="xl" align="stretch">
        {["", "Disabled", "Dark", "Darker", "Sky"].map((variant, i) => (
          <Grid.Col key={i} span={{ base: 12, sm: 6 }}>
            <CustomIconSelect
              label={`Icon Select ${variant}`}
              placeholder="Select icon"
              options={iconOptionsForPicker}
              disabled={variant === "Disabled"}
              variant={
                variant === "Dark"
                  ? "dark"
                  : variant === "Darker"
                    ? "darker"
                    : variant === "Sky"
                      ? "sky"
                      : "default"
              }
              value={selectedIcons[i] ?? null}
              onChange={(v) =>
                setSelectedIcons((prev) => ({ ...prev, [i]: v }))
              }
              showLabelInInput
              name={`iconSelect-${i}`}
            />
          </Grid.Col>
        ))}
      </Grid>

      {/* Dropzone */}
      <Group mb="xl" align="start">
        {[false, true].map((loading, i) => (
          <Dropzone
            key={i}
            onDrop={(files: File[]) => console.log(files)}
            accept={["image/png", "image/jpeg"]}
            maxSize={3 * 1024 ** 2}
            multiple={false}
            loading={loading}
          >
            <Group>
              <Box>
                <></>
                <Text size="xs">Drag images here</Text>
                <Text size="xs">or click to select files</Text>
              </Box>
            </Group>
          </Dropzone>
        ))}
      </Group>

      {/* DatasetSelect */}
      <Group mb="xl" align="start">
        <CustomDatasetSelect placeholder="Select dataset" data={mockDatasets} />
      </Group>
      {/* DatasetSelect */}
      <Group mb="xl" align="start">
        <CustomDatasetSelect placeholder="Select dataset" data={mockDatasets} />
      </Group>
      {/* Custom Output (rich text with mentions) */}
    </>
  );
};
